import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Card, Flex, Typography } from 'antd';

import { MAIN_GROUP_TYPE } from '../../../constants/group';
import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchFarms } from '../../../redux/actions/farm';
import { createLabel, fetchLabels } from '../../../redux/actions/label';

import CancelSaveButtons from '../../../components/CancelSaveButtons';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

import './styles.scss';

import GroupCreateForm from './GroupCreateForm';
import { DEFAULT_GROUP_COLOR } from './constants';

const { Title } = Typography;

function NewGroup({ history }) {
  const dispatch = useDispatch();

  const formRef = useRef(null);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    label: {
      name: '',
      id: null,
      color: '',
      animals: [],
      rules: []
    },
    new_label: {
      name: '',
      colour: DEFAULT_GROUP_COLOR,
      main_type: history.location.mainType || MAIN_GROUP_TYPE.VIRTUAL,
      filters: {}
    },
    selected_livestock: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      await getGroups();
      await getFarmsOptions();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getFarmsOptions() {
    await dispatch(fetchFarms()).unwrap();
  }

  async function getGroups() {
    await dispatch(fetchLabels({ farm_ids: [] })).unwrap();
  }

  async function saveLabel() {
    const response = await dispatch(createLabel({ ...state.new_label })).unwrap();

    successToastHandler(messages.CREATED);
    history.push(`/group/${response?.id}`);
    setIsFormDirty(false);
  }

  const setNewGroup = values => {
    setState(prevState => ({ ...prevState, new_label: { ...prevState.new_label, ...values } }));
  };

  const setNewGroupFilters = filters => {
    setState(prevState => ({
      ...prevState,
      new_label: { ...prevState.new_label, filters: { ...prevState.filters, ...filters } }
    }));
  };

  const breadcrumbItems = [
    {
      title: <Link to="/groups">List of Groups</Link>
    },
    {
      title: `Group ${state.label?.name}`
    }
  ];

  const handleCancelEditing = useCallback(() => {
    history.push('/groups');
  }, []);

  const { NavigationPrompt, modalProps, nextLocation, confirmed } = useUnsavedChangesPrompt({
    when: isFormDirty,
    onSaveAndLeave: async () => {
      if (submittable) {
        await saveLabel();

        return true;
      } else {
        warnToastHandler(messages.DATA_REQUIRED);
        formRef.current?.validateFields();

        return false;
      }
    },
    onDiscardAndLeave: () => {
      formRef.current?.resetFields();
      setIsFormDirty(false);
    }
  });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  return (
    <>
      {NavigationPrompt}
      <Flex gap="small" vertical={false} justify="space-between" wrap>
        <Flex vertical={true}>
          <Title level={4}>Group {state.label?.name}</Title>

          <AppBreadcrumb items={breadcrumbItems} />
        </Flex>
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancelEditing} handleSave={saveLabel} />
      </Flex>

      <Card title="Add new Group" className="mb-2">
        <GroupCreateForm
          formRef={formRef}
          collection={history?.location?.collection}
          handleChangeFormValues={setNewGroup}
          handleQueryBuilderChange={setNewGroupFilters}
          onChange={setIsFormDirty}
          setSubmittable={setSubmittable}
        />
      </Card>

      <Flex justify="end">
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancelEditing} handleSave={saveLabel} />
      </Flex>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default NewGroup;
